// On-screen keyboard awareness for dialogs and sheets (phones and tablets).
//
// iOS Safari and Chrome for Android do not resize the layout viewport when the
// keyboard opens: position:fixed boxes stay laid out for the full screen, so a
// centred dialog can end up half behind the keyboard. The Visual Viewport API
// says which part of the screen is really visible; this publishes it as CSS
// variables, and index.css uses them (html[data-keyboard="open"]).
//
// Nothing happens unless a text field has focus and the visible area has shrunk
// by more than a toolbar (pinch-zoom is ignored: scale !== 1). Without the API
// (old browsers) this is a no-op. Desktop never matches.

const MIN_KEYBOARD_PX = 120;
const EDITABLE = 'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=range]):not([type=file]), textarea, select, [contenteditable=""], [contenteditable="true"]';

export function initKeyboardViewport() {
  if (typeof window === 'undefined') return undefined;
  const vv = window.visualViewport;
  if (!vv) return undefined;
  const root = document.documentElement;
  let frame = 0;

  const clear = () => {
    if (root.dataset.keyboard) delete root.dataset.keyboard;
    ['--kb-h', '--kb-top', '--kb-bottom'].forEach((k) => root.style.removeProperty(k));
  };

  const update = () => {
    frame = 0;
    const active = document.activeElement;
    const editing = !!(active && active.matches && active.matches(EDITABLE));
    const shrunk = window.innerHeight - vv.height;
    if (editing && Math.abs(vv.scale - 1) < 0.01 && shrunk > MIN_KEYBOARD_PX) {
      root.style.setProperty('--kb-h', `${Math.round(vv.height)}px`);
      root.style.setProperty('--kb-top', `${Math.round(vv.offsetTop)}px`);
      root.style.setProperty('--kb-bottom', `${Math.max(0, Math.round(window.innerHeight - vv.offsetTop - vv.height))}px`);
      root.dataset.keyboard = 'open';
      // Inside a dialog or full-screen panel, bring the field being typed in back
      // into view: the box was just resized, and the browser's own scroll-into-view
      // ran before that. (Page-level fields are left to the browser.)
      if (active.closest && active.closest('[role="dialog"], .fixed.inset-0')) {
        const reveal = () => { if (document.activeElement === active) { try { active.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { /* ignore */ } } };
        window.requestAnimationFrame(reveal);
        window.setTimeout(reveal, 250); // once the keyboard animation has settled
      }
    } else {
      clear();
    }
  };
  const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };

  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', () => window.setTimeout(schedule, 50));
  return () => {
    vv.removeEventListener('resize', schedule);
    vv.removeEventListener('scroll', schedule);
    document.removeEventListener('focusin', schedule);
    clear();
  };
}
